/**
 * ESC/POS through the shop's print bridge (`infra/pos-print-bridge`, T07-25): a small FastAPI app
 * on a mini PC / Raspberry Pi in the shop that forwards bytes to a LAN printer on TCP 9100 — the
 * way for iPad Safari (no WebUSB / Web Bluetooth) and for printers that are only on the network.
 *
 * `POST {url}/print` (body = the bytes) · `POST {url}/drawer` · `GET {url}/status`, with the
 * bridge's token as `Authorization: Bearer …`. The bridge must be served over HTTPS (the POS page is
 * HTTPS — mixed content is blocked) — see its README.
 */
import { PrinterError, type PrinterDriver, type PrinterStatus } from "./types";

export interface BridgeOptions {
  /** e.g. `https://print-bridge.local:9443` */
  url: string;
  token?: string | null;
  /** Which printer of the bridge (its config name); default = the bridge's default. */
  printer?: string | null;
  timeoutMs?: number;
  fetch?: typeof fetch;
}

interface BridgeStatus {
  ok?: boolean;
  printer?: { name?: string; reachable?: boolean } | null;
}

export class BridgePrinter implements PrinterDriver {
  readonly kind = "network" as const;
  private readonly options: BridgeOptions;

  constructor(options: BridgeOptions) {
    this.options = options;
  }

  supported(): boolean {
    return Boolean(this.options.url);
  }

  private endpoint(path: string): string {
    const base = this.options.url.replace(/\/+$/, "");
    const query = this.options.printer
      ? `?printer=${encodeURIComponent(this.options.printer)}`
      : "";
    return `${base}${path}${query}`;
  }

  private async request(path: string, init: RequestInit): Promise<Response> {
    if (!this.options.url) {
      throw new PrinterError("not_found", "no bridge address");
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.options.timeoutMs ?? 8_000);
    const headers = new Headers(init.headers);
    if (this.options.token) {
      headers.set("Authorization", `Bearer ${this.options.token}`);
    }
    const fetcher = this.options.fetch ?? globalThis.fetch.bind(globalThis);
    try {
      const response = await fetcher(this.endpoint(path), {
        ...init,
        headers,
        signal: controller.signal,
      });
      if (response.status === 502 || response.status === 504) {
        throw new PrinterError(
          "disconnected",
          `printer not answering (${String(response.status)})`,
        );
      }
      if (!response.ok) {
        throw new PrinterError("write_failed", `bridge ${String(response.status)}`);
      }
      return response;
    } catch (cause) {
      if (cause instanceof PrinterError) {
        throw cause;
      }
      if (controller.signal.aborted) {
        throw new PrinterError("timeout");
      }
      throw new PrinterError(
        "bridge_unreachable",
        cause instanceof Error ? cause.message : undefined,
      );
    } finally {
      clearTimeout(timer);
    }
  }

  async connect(): Promise<PrinterStatus> {
    return this.status();
  }

  async print(bytes: Uint8Array): Promise<void> {
    await this.request("/print", {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: bytes as unknown as BodyInit,
    });
  }

  async openDrawer(): Promise<void> {
    await this.request("/drawer", { method: "POST" });
  }

  async status(): Promise<PrinterStatus> {
    try {
      const response = await this.request("/status", { method: "GET" });
      const body = (await response.json()) as BridgeStatus;
      const reachable = Boolean(body.printer?.reachable ?? body.ok);
      return {
        connected: reachable,
        name: body.printer?.name ?? null,
        error: reachable ? null : "disconnected",
      };
    } catch (error) {
      return {
        connected: false,
        error: error instanceof PrinterError ? error.code : "bridge_unreachable",
      };
    }
  }

  async disconnect(): Promise<void> {
    // stateless HTTP — nothing to close
  }
}
